import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'antd';
import { CheckOutlined, CloseOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { benefitGroups, introduction, PAGE_TITLE, tiers } from './benefits';
import './style.scss';

function BenefitValue({ value }) {
  if (typeof value !== 'boolean') return <span className="jumeirah-value-text">{value}</span>;

  return (
    <span className={`jumeirah-status${value ? '' : ' jumeirah-status-unavailable'}`}>
      {value ? <CheckOutlined aria-hidden="true" /> : <CloseOutlined aria-hidden="true" />}
      <span className="jumeirah-sr-only">{value ? '享有' : '不享有'}</span>
    </span>
  );
}

export default function JumeirahBenefits() {
  const [selected, setSelected] = useState(null);
  const pageRef = useRef(null);
  const isOpen = Boolean(selected);

  useEffect(() => {
    if (!isOpen) return undefined;
    // Modal locks the body; the application's H5 shell scrolls #container.
    const container = pageRef.current.closest('#container');
    if (!container) return undefined;
    const previousOverflow = container.style.overflowY;
    container.style.overflowY = 'hidden';
    return () => { container.style.overflowY = previousOverflow; };
  }, [isOpen]);

  return (
    <main className="jumeirah-benefits" ref={pageRef}>
      <header className="jumeirah-heading">
        <h1 aria-label={PAGE_TITLE}><span>Jumeirah One</span><span>会员礼遇</span></h1>
        <div className="jumeirah-heading-rule" aria-hidden="true" />
        <p>{introduction}</p>
      </header>
      <h2 className="jumeirah-subtitle">会员等级</h2>
      <div className="jumeirah-comparison" role="table" aria-label="会员等级礼遇对照表" aria-colcount={5}>
        <div className="jumeirah-tier-header" role="row">
          <span className="jumeirah-sr-only" role="columnheader" aria-colindex={1}>礼遇</span>
          {tiers.map((tier, index) => <span role="columnheader" aria-colindex={index + 2} key={tier}>{tier}</span>)}
        </div>
        {benefitGroups.map((group) => (
          <div className={`jumeirah-group jumeirah-group-${group.id}`} role="rowgroup" aria-label={group.title || group.accessibleTitle} key={group.id}>
            {group.title && (
              <div role="row" className="jumeirah-group-heading">
                <div role="cell" aria-colspan={5}><h2>{group.title}</h2></div>
              </div>
            )}
            {group.rows.map((benefit) => (
              <div className="jumeirah-benefit-row" role="row" key={benefit.id}>
                <div role="rowheader" aria-colindex={1}>
                  <button type="button" className="jumeirah-detail-trigger" aria-label={`查看${benefit.title}详情`} aria-haspopup="dialog" onClick={() => setSelected(benefit)}>
                    <span>{benefit.title}</span><InfoCircleOutlined aria-hidden="true" />
                  </button>
                </div>
                <div className={`jumeirah-values${benefit.id === 'tier-points' ? ' jumeirah-point-ranges' : ''}`} role="presentation">
                  {benefit.values.map((value, index) => (
                    <div role="cell" aria-colindex={index + 2} key={tiers[index]}><BenefitValue value={value} /></div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
      <Modal
        open={isOpen}
        title={selected?.title}
        onCancel={() => setSelected(null)}
        footer={null}
        closable={false}
        centered
        width={420}
        rootClassName="jumeirah-modal"
      >
        <button className="jumeirah-detail-close" type="button" title="关闭权益详情" aria-label="关闭权益详情" onClick={() => setSelected(null)}><CloseOutlined aria-hidden="true" /></button>
        {selected && (
          <div className="jumeirah-detail-content">
            {selected.description && <p>{selected.description}</p>}
            <dl className="jumeirah-detail-values">
              {selected.values.map((value, index) => (
                <div key={tiers[index]}>
                  <dt>{tiers[index]}</dt>
                  <dd>{typeof value === 'boolean' ? (value ? '享有' : '不享有') : value}</dd>
                </div>
              ))}
            </dl>
            {selected.notes && <div className="jumeirah-detail-notes">{selected.notes.map((note) => <p key={note}>{note}</p>)}</div>}
          </div>
        )}
      </Modal>
    </main>
  );
}
